import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useWalletsReadContext } from '@context/UserWalletContext'

import { Grid } from '@ui/Layouts/Grid/Grid'
import { PageSection } from '@ui/components/PageSection'
import { ArrowLeftRightIcon } from '@ui/icons/ArrowLeftRight.icon'
import { DepositIcon } from '@ui/icons/Deposit.icon'
import { ExchangeIcon } from '@ui/icons/Exchange.icon'
import { WithdrawalIcon } from '@ui/icons/Withdrawal.icon'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'
import { WalletRestrictions, isDepositEnabledFromAction, isRestricted } from '@utils/wallet.utils'

import { TotalWalletBalanceDto } from '@model/TotalWalletBalanceDto'
import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'
import { useScrollToTop } from '@hooks/useScrollToTop'
import { useTradingAccountStatus } from '@hooks/useTradingAccountStatus'
import { useWithdrawalPageNavigation } from '@hooks/useWithdrawalPageNavigation'

import { FirstTimeGuideCarousel } from '@shared/components/FirstTimeGuideCarousel/FirstTimeGuideCarousel'
import { Loading } from '@shared/components/Loading/Loading'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { Card } from './Card'

interface TransactionCardsProps {
  areWalletsLoading: boolean
  walletWithdrawLoadingId: string
  canDeposit: boolean
  handleWithdrawalNavigation: () => Promise<void>
}

const useTransactionsFetch = () => {
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.walletExtended.totalBalance(account?.displayCurrency?.id ?? 'USD'),
    queryFn: () =>
      apiClient.getTotalBalanceByCurrencyBothProducts(account?.displayCurrency?.id ?? 'USD'),
    refetchOnMount: 'always',
    enabled: !!account?.displayCurrency?.id,
  })

  return { data, isLoading }
}

const useTransactionsData = (data?: TotalWalletBalanceDto) => {
  const { t } = useTranslation()
  const { isDefaultCFDProductType } = useProductReadContext()
  const { formatMoney } = useFormatNumber()

  const balance = data?.currency?.id ? `≈${formatMoney(data.balance, data.currency.id)}` : ''

  const description = !isDefaultCFDProductType()
    ? t('Transactions.Wallets balances', {
        balance,
      })
    : undefined

  return {
    balance,
    description,
  }
}

export const TransactionsPage: React.FC = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const { handleWithdrawalNavigation, walletWithdrawLoadingId } = useWithdrawalPageNavigation()
  const { isDefaultCFDProductType } = useProductReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const walletType = isDefaultCFDProductType() ? WalletTypeEnum.CFD : WalletTypeEnum.ETD

  const { data: wallets = [], isLoading: areWalletsLoading } = useQuery({
    queryKey: queryKeys.wallet.byType(walletType),
    queryFn: () => apiClient.getWallets({ walletType }),
    refetchOnMount: 'always',
  })

  const hasCanDepositWallet = wallets.some((wallet) => {
    return isDepositEnabledFromAction(wallet.depositAction)
  })
  const hasUnrestrictedWallet = wallets.some(
    (wallet) => !isRestricted(WalletRestrictions.DEPOSIT, wallet.restrictions)
  )
  const canDeposit = hasCanDepositWallet || hasUnrestrictedWallet

  const transactionCardProps = {
    handleWithdrawalNavigation,
    walletWithdrawLoadingId,
    areWalletsLoading,
    canDeposit,
  }

  return (
    <>
      <FirstTimeGuideCarousel disabled />
      <PageSection title={t('Transactions.Transactions')} />
      {isDefaultCFDProductType() && <TransactionsCardsCFD {...transactionCardProps} />}
      {!isDefaultCFDProductType() && <TransactionsCardsETD {...transactionCardProps} />}
    </>
  )
}

const TransactionsCardsCFD = ({
  areWalletsLoading,
  canDeposit,
  handleWithdrawalNavigation,
  walletWithdrawLoadingId,
}: TransactionCardsProps) => {
  const { t } = useTranslation()
  const {
    hasNegativeBalance,
    isLoading: negativeBalanceLoading,
    hasActiveTradingAccounts,
  } = useTradingAccountStatus()

  const { data, isLoading } = useTransactionsFetch()
  const { description } = useTransactionsData(data)
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const tracker = useDepositTracker()
  const { checkAccountTradersRoomAccess, checkAccountOpenAccountAccess } =
    useAccountActionAccessCheck()

  const { wallets = [] } = useWalletsReadContext()

  const handleWithdrawalClick = () => {
    checkAccountTradersRoomAccess(() => {
      handleWithdrawalNavigation()
    })
  }

  const handleTransferClick = () => {
    if (!hasActiveTradingAccounts && !!wallets.length) {
      return navigate(CommonPaths.NewLiveTradingAccount)
    }
    checkAccountOpenAccountAccess(() => {
      navigate(`${CommonPaths.TradersRoomWallets}/transfer`)
    })
  }

  const handleDepositClick = () => {
    tracker.start({ entry_point: 'transactions' })
    navigateToFunding(`${CommonPaths.Deposit}/wallet`)
  }

  return (
    <Loading showLoadingIcon isLoading={isLoading || areWalletsLoading}>
      <Grid maxCardWidth={300} mobileSameSize>
        <Card
          title={t('Transactions.Deposit')}
          renderDescription={() => <p>{t('Transactions.Move Funds')}</p>}
          buttonText={t('Transactions.Start Deposit')}
          variant='primary'
          lockAction={!canDeposit}
          onClick={handleDepositClick}
          additionalDescription={description}
          icon={<DepositIcon size={80} />}
          buttonInfoText={t('Temporarily unavailable')}
        />
        <Card
          title={t('Transactions.Withdraw')}
          renderDescription={() => <p>{t('Transactions.Move funds wallet bank')}</p>}
          buttonText={t('Transactions.Start Withdrawal')}
          onClick={handleWithdrawalClick}
          additionalDescription={description}
          buttonInfoText={t(
            'Wallet.Withdrawal is currently not available because your trading account'
          )}
          lockAction={hasNegativeBalance || negativeBalanceLoading}
          icon={<WithdrawalIcon size={80} />}
          isLoading={!!walletWithdrawLoadingId}
        />
        <Card
          title={t('Transactions.Transfer Funds')}
          renderDescription={() => <p>{t('Transactions.Move funds between')}</p>}
          buttonText={t('Transactions.Start Transfer')}
          onClick={handleTransferClick}
          icon={<ArrowLeftRightIcon size={80} />}
        />
      </Grid>
    </Loading>
  )
}

const TransactionsCardsETD = ({
  areWalletsLoading,
  canDeposit,
  handleWithdrawalNavigation,
}: TransactionCardsProps) => {
  const { t } = useTranslation()
  const { navigateToFunding } = useFundingNav()
  const tracker = useDepositTracker()

  const { data, isLoading } = useTransactionsFetch()
  const { description } = useTransactionsData(data)

  const handleDepositClick = () => {
    tracker.start({ entry_point: 'transactions' })
    navigateToFunding(`${CommonPaths.Deposit}/wallet`)
  }

  return (
    <Loading showLoadingIcon isLoading={isLoading || areWalletsLoading}>
      <Grid mobileSameSize>
        <Card
          bottomSpaced
          title={t('Transactions.Deposit')}
          renderDescription={() => <p>{t('Transactions.Move Funds')}</p>}
          buttonText={t('Transactions.Start Deposit')}
          variant='primary'
          onClick={handleDepositClick}
          additionalDescription={description}
          icon={<DepositIcon size={80} />}
          lockAction={!canDeposit}
          buttonInfoText={t('Temporarily unavailable')}
        />
        <Card
          bottomSpaced
          title={t('Transactions.Withdraw')}
          renderDescription={() => <p>{t('Transactions.Move funds wallet bank')}</p>}
          buttonText={t('Transactions.Start Withdrawal')}
          onClick={handleWithdrawalNavigation}
          additionalDescription={description}
          icon={<WithdrawalIcon size={80} />}
        />
        <Card
          bottomSpaced
          title={t('Transactions.Transfer Funds')}
          renderDescription={() => <p>{t('Transactions.Move funds between')}</p>}
          buttonText={t('Transactions.Start Transfer')}
          to={`${CommonPaths.TradersRoomWallets}/transfer`}
          icon={<ArrowLeftRightIcon size={80} />}
        />
        <Card
          bottomSpaced
          title={t('Transactions.Conversion')}
          renderDescription={() => <p>{t('Transactions.Convert currencies')}</p>}
          buttonText={t('Transactions.Start Conversion')}
          to={'/dashboard/traders-room/transactions/conversion'}
          icon={<ExchangeIcon size={80} />}
        />
      </Grid>
    </Loading>
  )
}
